import { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, GitBranch, TrendingUp, Upload, LogOut, UsersRound, Menu, X, DollarSign, Settings, Receipt } from "lucide-react";
import { LOGO_WHITE, LOGO_ICON } from "@/lib/brand";

const nav = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true, id: "overview", roles: ["admin", "staff", "sales", "affiliate"] },
  { to: "/referrals", label: "Referrals", icon: GitBranch, id: "referrals", roles: ["admin", "staff", "sales"] },
  { to: "/customers", label: "Customers", icon: Users, id: "customers", roles: ["admin", "staff", "sales", "affiliate"] },
  { to: "/transactions", label: "Transactions", icon: Receipt, id: "transactions", roles: ["admin", "staff", "sales", "affiliate"] },
  { to: "/profit", label: "Profit", icon: DollarSign, id: "profit", roles: ["admin", "staff", "sales", "affiliate"] },
  { to: "/growth", label: "Growth", icon: TrendingUp, id: "growth", roles: ["admin", "staff", "sales", "affiliate"] },
  { to: "/import", label: "Import Data", icon: Upload, id: "import", roles: ["admin", "staff"] },
  { to: "/users", label: "Users & Roles", icon: UsersRound, id: "users", roles: ["admin", "staff"] },
  { to: "/settings", label: "Settings", icon: Settings, id: "settings", roles: ["admin", "staff"] },
];

function SidebarBody({ user, onNavigate, onLogout }) {
  const items = nav.filter((n) => !n.roles || n.roles.includes(user?.role));
  return (
    <div className="flex h-full flex-col" style={{ backgroundColor: "var(--navy)" }}>
      <div className="flex items-center border-b border-white/10 px-6 py-5">
        <img src={LOGO_WHITE} alt="XlentPay" className="h-7 w-auto" />
      </div>
      <div className="px-6 pb-1 pt-4">
        <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/40">Intelligence Dashboard</span>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-3">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onNavigate}
            data-testid={`nav-${n.id}`}
            className={({ isActive }) =>
              `group relative flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                isActive ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            {({ isActive }) => (
              <>
                {isActive && <span className="brand-gradient absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r" />}
                <n.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
                <span className="truncate">{n.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 px-4 py-4">
        <div className="mb-2 grid grid-cols-[1fr_auto] items-center gap-2" data-testid="sidebar-user-email">
          <span className="truncate text-xs text-white/60">{user?.email}</span>
          <span className="brand-gradient shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#0e2e3d]">
            {user?.role}
          </span>
        </div>
        <button
          onClick={onLogout}
          data-testid="logout-button"
          className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-white/60 transition-colors duration-200 hover:bg-white/5 hover:text-white"
        >
          <LogOut className="h-4 w-4 shrink-0" /> Sign out
        </button>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden w-60 shrink-0 md:block">
        <SidebarBody user={user} onLogout={handleLogout} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64 max-w-[80%] shadow-xl">
            <button
              onClick={() => setMobileOpen(false)}
              data-testid="close-mobile-nav"
              className="absolute right-3 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-md text-white/70 hover:bg-white/10"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarBody user={user} onLogout={handleLogout} onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between gap-3 border-b border-border bg-card px-4 py-3 md:px-6">
          <div className="flex min-w-0 items-center gap-2">
            <button
              onClick={() => setMobileOpen(true)}
              data-testid="open-mobile-nav"
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground hover:bg-accent md:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            <img src={LOGO_ICON} alt="XlentPay" className="h-7 w-7 md:hidden" />
            <span className="hidden text-sm text-muted-foreground md:block">Weekly referral &amp; client intelligence</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="brand-gradient flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-[#0e2e3d]">
              {(user?.name || user?.email || "A").slice(0, 1).toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
